import { hashString } from './dates';

/**
 * Cores que são dado, não token: a paleta que o administrador escolhe para cada
 * ministério (ou uma cor livre, pelo seletor de cor) e a cor de cada série de eventos. Entram na interface por
 * `style` — o scanner do Tailwind não gera classe montada em runtime.
 */

export interface PaletteColor {
  color: string;
  tint: string;
  name: string;
  /**
   * O par no tema escuro: o fundo vem do mapa do protótipo (cada tint 50 tem o seu tom
   * escuro); a cor, clareada (tom 400), é a de texto e ícone sobre esse fundo. Ponto,
   * barra e borda continuam com `color`, que se lê bem nos dois temas.
   */
  dark: { color: string; tint: string };
}

/** 24 cores (tom 600 sobre tint 50), com nome para leitores de tela e o par escuro. */
export const MINISTRY_PALETTE: readonly PaletteColor[] = [
  { color: '#4F46E5', tint: '#EEF2FF', name: 'Índigo', dark: { color: '#818CF8', tint: '#232456' } },
  { color: '#16A34A', tint: '#F0FDF4', name: 'Verde', dark: { color: '#4ADE80', tint: '#0F2A1A' } },
  { color: '#0891B2', tint: '#ECFEFF', name: 'Ciano', dark: { color: '#22D3EE', tint: '#0B2A30' } },
  { color: '#DB2777', tint: '#FDF2F8', name: 'Rosa', dark: { color: '#F472B6', tint: '#33122A' } },
  { color: '#D97706', tint: '#FFFBEB', name: 'Âmbar', dark: { color: '#FBBF24', tint: '#2B210B' } },
  { color: '#7C3AED', tint: '#F5F3FF', name: 'Violeta', dark: { color: '#A78BFA', tint: '#221A40' } },
  { color: '#2563EB', tint: '#EFF6FF', name: 'Azul', dark: { color: '#60A5FA', tint: '#0F213F' } },
  { color: '#0284C7', tint: '#F0F9FF', name: 'Azul-céu', dark: { color: '#38BDF8', tint: '#0B2536' } },
  { color: '#0D9488', tint: '#F0FDFA', name: 'Verde-azulado', dark: { color: '#2DD4BF', tint: '#0E2A27' } },
  { color: '#059669', tint: '#ECFDF5', name: 'Esmeralda', dark: { color: '#34D399', tint: '#0F2A1F' } },
  { color: '#65A30D', tint: '#F7FEE7', name: 'Lima', dark: { color: '#A3E635', tint: '#1E2A0E' } },
  { color: '#4D7C0F', tint: '#F7FEE7', name: 'Oliva', dark: { color: '#84CC16', tint: '#1E2A0E' } },
  { color: '#CA8A04', tint: '#FEFCE8', name: 'Amarelo', dark: { color: '#FACC15', tint: '#2A250B' } },
  { color: '#EA580C', tint: '#FFF7ED', name: 'Laranja', dark: { color: '#FB923C', tint: '#2E1A0C' } },
  { color: '#DC2626', tint: '#FEF2F2', name: 'Vermelho', dark: { color: '#F87171', tint: '#2A1215' } },
  { color: '#E11D48', tint: '#FFF1F2', name: 'Carmim', dark: { color: '#FB7185', tint: '#33121A' } },
  { color: '#BE185D', tint: '#FDF2F8', name: 'Magenta', dark: { color: '#EC4899', tint: '#33122A' } },
  { color: '#C026D3', tint: '#FDF4FF', name: 'Fúcsia', dark: { color: '#E879F9', tint: '#311536' } },
  { color: '#9333EA', tint: '#FAF5FF', name: 'Roxo', dark: { color: '#C084FC', tint: '#261A3D' } },
  { color: '#6D28D9', tint: '#F5F3FF', name: 'Uva', dark: { color: '#8B5CF6', tint: '#221A40' } },
  { color: '#1E40AF', tint: '#EFF6FF', name: 'Marinho', dark: { color: '#93C5FD', tint: '#0F213F' } },
  { color: '#92400E', tint: '#FFFBEB', name: 'Marrom', dark: { color: '#FCD34D', tint: '#2B210B' } },
  { color: '#475569', tint: '#F8FAFC', name: 'Ardósia', dark: { color: '#A3B1C6', tint: '#172036' } },
  { color: '#44403C', tint: '#FAFAF9', name: 'Grafite', dark: { color: '#D6D3D1', tint: '#1C1B19' } },
];

const FALLBACK: PaletteColor = MINISTRY_PALETTE[0]!;

/** Nome da cor escolhida fora da paleta, no seletor de cor. */
export const CUSTOM_COLOR_NAME = 'Personalizada';

const HEX = /^#[0-9A-F]{6}$/i;

/** Fundo das páginas no tema escuro (`--dark-page`), base do tint escuro. */
const DARK_BASE = '#0B1120';

/** Contraste mínimo do texto e do ícone sobre o tint (WCAG AA, texto normal). */
const MIN_CONTRAST = 4.5;

function channels(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);

  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(rgb: readonly number[]): string {
  return `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/** `amount` de `a` sobre `b` (0 = só `b`, 1 = só `a`), no sRGB, como o `color-mix`. */
function mix(a: string, b: string, amount: number): string {
  const [ca, cb] = [channels(a), channels(b)];

  return toHex(ca.map((c, i) => c * amount + cb[i]! * (1 - amount)));
}

function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const v = c / 255;

    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** Contraste WCAG entre duas cores (de 1 a 21). */
export function contrastRatio(a: string, b: string): number {
  const [la, lb] = [luminance(a), luminance(b)].sort((x, y) => y - x);

  return (la! + 0.05) / (lb! + 0.05);
}

/** Aproxima `color` de `toward` (preto ou branco) até ler bem sobre `background`. */
function readable(color: string, background: string, toward: string): string {
  for (let step = 0; step <= 10; step++) {
    const candidate = mix(toward, color, step / 10);

    if (contrastRatio(candidate, background) >= MIN_CONTRAST) return candidate;
  }

  return toward;
}

/**
 * A paleta de uma cor fora das 24 (o seletor de cor livre): o tint claro é a cor bem
 * diluída no branco e o escuro, no fundo do tema escuro, como os do protótipo; a cor do
 * texto e do ícone escurece (no claro) ou clareia (no escuro) só o que precisar para
 * ler bem sobre o tint. Ponto, barra e borda continuam com a cor escolhida.
 */
export function customPalette(color: string): PaletteColor {
  const base = color.toUpperCase();
  const tint = mix(base, '#FFFFFF', 0.08);
  const darkTint = mix(base, DARK_BASE, 0.28);

  return {
    color: readable(base, tint, '#000000'),
    tint,
    name: CUSTOM_COLOR_NAME,
    dark: { color: readable(mix(base, '#FFFFFF', 0.65), darkTint, '#FFFFFF'), tint: darkTint },
  };
}

/** Uma das 24 cores; outra cor válida ganha a paleta derivada (`customPalette`). */
export function paletteOf(color: string): PaletteColor {
  const preset = MINISTRY_PALETTE.find((p) => p.color.toLowerCase() === color.toLowerCase());

  if (preset) return preset;

  return HEX.test(color) ? customPalette(color) : FALLBACK;
}

/**
 * Séries de exemplo têm cor fixa; as criadas depois recebem uma cor estável pelo id. As
 * três que se repetem seguem a ordem dos tipos de culto nos gráficos da Membresia
 * (índigo, ciano, âmbar — `client/src/pages/Membership/chartColors.ts`), para o culto ter
 * a mesma cor no calendário e no gráfico.
 */
const SERIES_COLORS: Record<string, PaletteColor> = {
  'dom-m': MINISTRY_PALETTE[0]!,
  'dom-n': MINISTRY_PALETTE[2]!,
  qua: MINISTRY_PALETTE[4]!,
  conf: MINISTRY_PALETTE[3]!,
  bat: MINISTRY_PALETTE[1]!,
};

export function seriesColor(seriesId: string): PaletteColor {
  return SERIES_COLORS[seriesId] ?? MINISTRY_PALETTE[hashString(seriesId) % MINISTRY_PALETTE.length]!;
}

/** A cor do evento: a escolhida (com o par do tema escuro) ou, sem ela, a automática da série. */
export function eventColor(event: { seriesId: string; color: string | null }): PaletteColor {
  return event.color ? paletteOf(event.color) : seriesColor(event.seriesId);
}

/** "Paula R." */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);

  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1]?.[0]}.` : (parts[0] ?? '');
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}
